/**
 * Point 29 — what is in flight, visible from the inbox.
 *
 * Three counts across the top of the mail screen: out at RFQ, waiting to be compared, quoted and
 * waiting on the customer. Each opens the board filtered to exactly the statuses it counted, so a
 * number is never a dead end.
 *
 * ---- Its own component, and its own hook ----
 *
 * `MailPage` returns early while the mailboxes load, so a `useEnquiries` call added up there would
 * sit after an early return — the hook-order crash this codebase has now hit three times. Holding
 * the query inside this component keeps it unconditional where it lives, and has the side benefit
 * that a failed enquiry read cannot take the mailbox down with it.
 *
 * Silent on failure and while loading: this is an ambient readout, not the screen's job. A red
 * error bar across the top of the inbox because a background count did not resolve would be worse
 * than the missing count.
 */

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useEnquiries } from '../../lib/api/useEnquiries'
import { inboxCounts, INBOX_COUNT_STATUSES, type InboxCounts } from '../../lib/inboxCounts'

const TILES: { key: keyof InboxCounts; label: string; icon: string; tone: string }[] = [
  { key: 'rfq', label: 'Out at RFQ', icon: '📤', tone: 'var(--brand)' },
  { key: 'comparison', label: 'To compare', icon: '⚖️', tone: 'var(--violet)' },
  { key: 'quotation', label: 'Quoted', icon: '📄', tone: 'var(--teal)' },
]

export function InboxPipelineStrip() {
  const navigate = useNavigate()
  // The same page size the board uses. These are counts OF THE PAGE, like every other count in
  // this app that reads the enquiry list — see `requirementFilters.ts`'s note on the same trade.
  const query = useEnquiries({ limit: 200 })
  const counts = useMemo(() => inboxCounts(query.data?.data ?? []), [query.data])

  // Nothing to say yet, or nothing came back. Renders nothing rather than three zeroes, which
  // would read as "there is no work" when the truth is "we have not looked".
  if (query.isPending || !query.data) return null

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
      {TILES.map((tile) => {
        const value = counts[tile.key]
        return (
          <button
            key={tile.key}
            type="button"
            // Opens the board where the work is. `INBOX_COUNT_STATUSES` is the same list the count
            // was computed from, so what opens is what was counted.
            onClick={() => navigate(`/requirements?status=${encodeURIComponent(INBOX_COUNT_STATUSES[tile.key].join(','))}`)}
            title={`${value} · ${INBOX_COUNT_STATUSES[tile.key].join(', ')}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 12px',
              border: '1px solid var(--line)',
              background: 'var(--panel)',
              borderRadius: 10,
              cursor: 'pointer',
              // A zero is real information ("nothing is out at RFQ") but not something to draw the
              // eye to, so it recedes rather than disappearing.
              opacity: value === 0 ? 0.55 : 1,
            }}
          >
            <span>{tile.icon}</span>
            <span style={{ fontWeight: 800, fontSize: 16, color: tile.tone }}>{value}</span>
            <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>{tile.label}</span>
          </button>
        )
      })}
    </div>
  )
}
